/**
 * This agent's FACE — the surface it presents when it is the thing on screen.
 *
 * An agent has two representations and one implementation: in-frame (the main
 * app's Home detail pane) and full-screen (this app at its own subdomain, where
 * pages/Stay.tsx mounts THIS module rather than the board underneath it). The
 * module is how an agent is packaged; the representation is only where it is
 * mounted, and a page that reached past the face to the board is how the two
 * come to differ (agentFaces.test.ts asserts no app does).
 *
 * The main app finds this file by CONVENTION: it globs `apps/<slug>/src/face.tsx`
 * and keys the result on the directory name (frontend/src/components/agentDetail/
 * agentDetailViews.tsx). Same filesystem discovery frontend/Dockerfile.local and
 * build.yml use to decide which apps to build, so a new agent needs no edit in
 * the platform.
 *
 * THE FACE RESOLVES THE SUBJECT, and there are two ways it arrives:
 *
 *   - full-screen, the stay is IN THE PATH (`/stays/{id}`, adr/0023), and the
 *     page hands it straight down;
 *   - in the frame there is no path. Home's pane says which PATIENT the nurse
 *     is on, through the conversation it opened or through the pane's own
 *     patient frame, and the face resolves that patient's admitted stay.
 *
 * The second is a resolution, not a guess: `admittedStay` refuses to choose
 * when a patient has two open stays, and answers null when she has none.
 *
 * The board itself is NOT here. A care plan is a platform capability; this file
 * is only the binding of that capability to THIS agent.
 */
import { useTranslation } from 'react-i18next'

import type { SessionResponse } from '@frontend/api/types_session'
import type { SessionDetail } from '@frontend/hooks/useSessions'
import type { AgentFaceProps } from '@frontend/components/agentDetail/agentFace'
import { CarePlanBoardConnected } from '@frontend/components/carePlan/CarePlanBoardConnected'
import { Spinner } from '@frontend/components/ui/spinner'
import { useAdmittedStay } from '@frontend/hooks/useEncounters'

import { AGENT_SLUG } from './agent'

/**
 * The platform's face props, with two changes this agent's face genuinely
 * needs:
 *
 *   - `session` is optional, because Home mounts the face session-less with a
 *     patient frame and the composer inside the board mints the conversation on
 *     the first message. The platform's type declares it required for the chat
 *     view, which does read it on its first line.
 *   - `stayId` exists, because the full-screen surface already knows the
 *     subject. The platform never passes it and never needs to know about it.
 */
export type CarePlannerFaceProps = Omit<AgentFaceProps, 'session'> & {
  session?: SessionDetail
  stayId?: string
}

function Message({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full items-center justify-center p-8 text-center">
      <p className="max-w-sm text-[17px] text-[var(--color-surface-mute)]">{children}</p>
    </div>
  )
}

/**
 * The in-frame shape: no stay in hand, so resolve the patient's admitted one.
 *
 * A separate component and not a branch inside Face, so the resolution's hooks
 * only run on the surface that needs them. The full-screen page already has the
 * stay and must not pay for a stay list to learn what its own URL said.
 */
function FaceOnPatient({
  patientId,
  session,
  onSessionCreated,
}: {
  patientId: string
  session?: SessionDetail
  onSessionCreated?: (created: SessionResponse) => void
}) {
  const { t } = useTranslation()
  const { data: stay, isLoading, error } = useAdmittedStay(patientId)

  if (error) {
    return (
      <div className="flex h-full items-center justify-center p-8 text-center">
        <p className="max-w-sm text-[17px] text-[var(--color-status-danger,#b3261e)]">
          {t('carePlan.stayUnresolved', "This patient's stay could not be read.")}
        </p>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <Spinner className="h-6 w-6 text-[var(--color-surface-mute)]" />
        <span className="sr-only">{t('common.loading', 'Loading...')}</span>
      </div>
    )
  }

  if (!stay) {
    return (
      <Message>
        {t(
          'carePlan.patientNotAdmitted',
          'This patient is not admitted, so there is no stay to plan.',
        )}
      </Message>
    )
  }

  return (
    <CarePlanBoardConnected
      stayId={stay.id}
      session={session}
      onSessionCreated={onSessionCreated}
      agentSlug={AGENT_SLUG}
    />
  )
}

export default function Face({
  session,
  facePatientId,
  onSessionCreated,
  stayId,
}: CarePlannerFaceProps) {
  const { t } = useTranslation()

  if (stayId) {
    return (
      <CarePlanBoardConnected
        stayId={stayId}
        session={session}
        onSessionCreated={onSessionCreated}
        agentSlug={AGENT_SLUG}
      />
    )
  }

  // One subject, named two ways by the two mount shapes in the frame: a face
  // bound to a conversation is about that conversation's patient, and a
  // session-less face is about the pane's framed patient. Never both at once,
  // so there is nothing to reconcile.
  const subject = session?.session.subject
  const patientId = subject?.type === 'patient' ? subject.id : facePatientId
  if (!patientId) {
    return (
      <Message>
        {t('carePlan.faceNeedsPatient', 'A care plan belongs to a patient. Open one from the ward.')}
      </Message>
    )
  }

  return (
    <FaceOnPatient
      patientId={patientId}
      session={session}
      onSessionCreated={onSessionCreated}
    />
  )
}
